import type { PersonalityId, SkillId } from '@shared/protocol/messages';

export interface HeroClass {
  id: string;
  name: string;
  blurb: string;
  base: { hp: number; atk: number; def: number; luck: number };
  skill: SkillId;
  color: string;
}

export const HERO_CLASSES: HeroClass[] = [
  {
    id: 'pencil_knight',
    name: 'Pencil Knight',
    blurb: 'Red scarf, steel sword, notebook shield. Snips through armor with Scissors.',
    base: { hp: 40, atk: 10, def: 3, luck: 3 },
    skill: 'sharp_edge',
    color: '#2e86de',
  },
  {
    id: 'crayon_mage',
    name: 'Crayon Mage',
    blurb: 'Draws spells in wax. Rock wins hit extra hard.',
    base: { hp: 34, atk: 12, def: 2, luck: 5 },
    skill: 'crayon_smash',
    color: '#8e5bd6',
  },
  {
    id: 'eraser_ninja',
    name: 'Eraser Ninja',
    blurb: 'Rubs out attacks before they land. Lucky and slippery.',
    base: { hp: 36, atk: 9, def: 3, luck: 8 },
    skill: 'doodle_dodge',
    color: '#ff8fab',
  },
  {
    id: 'ruler_guard',
    name: 'Ruler Guard',
    blurb: 'Thirty centimetres of pure defence. Paper shields the blow.',
    base: { hp: 48, atk: 8, def: 5, luck: 2 },
    skill: 'paper_shield',
    color: '#6ab04c',
  },
  {
    id: 'glue_alchemist',
    name: 'Glue Alchemist',
    blurb: 'Mixes glue potions in a test tube. Combos just get stuck.',
    base: { hp: 42, atk: 9, def: 4, luck: 3 },
    skill: 'glue_guard',
    color: '#f3a712',
  },
  {
    id: 'highlighter_scout',
    name: 'Highlighter Scout',
    blurb: 'Marks every weak spot in neon. Each hit recharges the ink.',
    base: { hp: 36, atk: 11, def: 2, luck: 6 },
    skill: 'highlighter_drain',
    color: '#3caea3',
  },
];

export function heroClass(id: string): HeroClass {
  return HERO_CLASSES.find((c) => c.id === id) ?? HERO_CLASSES[0]!;
}

export interface PresetMonster {
  id: string;
  name: string;
  blurb: string;
  hp: number;
  atk: number;
  def: number;
  luck: number;
  skill: SkillId;
  personality: PersonalityId;
  tier: 'doodle' | 'sketch' | 'masterpiece';
  reward: { exp: number; coins: number };
  /** Item granted the first time this monster is defeated. */
  firstWinItem?: string;
  /** Hero level suggested before fighting. */
  level: number;
}

/** Built-in monsters drawn by "the kid who sits at the back of class". */
export const PRESET_MONSTERS: PresetMonster[] = [
  { id: 'gum_blob', name: 'Gum Blob', blurb: 'Found under desk #4. Sticky and stubborn.', hp: 23, atk: 6, def: 2, luck: 2, skill: 'none', personality: 'stubborn', tier: 'doodle', reward: { exp: 22, coins: 10 }, level: 1, firstWinItem: 'cardboard_shield' },
  { id: 'homework_ghost', name: 'Homework Ghost', blurb: 'Haunts anyone who forgot page 37.', hp: 28, atk: 8, def: 2, luck: 4, skill: 'eraser_mend', personality: 'shy', tier: 'sketch', reward: { exp: 24, coins: 12 }, level: 2, firstWinItem: 'lucky_sticker' },
  { id: 'spitball_spider', name: 'Spitball Spider', blurb: 'Eight legs, five eyes, zero manners.', hp: 42, atk: 14, def: 6, luck: 8, skill: 'none', personality: 'sharp', tier: 'sketch', reward: { exp: 38, coins: 18 }, level: 3, firstWinItem: 'pencil_sword' },
  { id: 'eraser_golem', name: 'Eraser Golem', blurb: 'A green eraser beast with a pink cap. Rubs out whole chapters.', hp: 46, atk: 11, def: 5, luck: 3, skill: 'paper_shield', personality: 'copycat', tier: 'sketch', reward: { exp: 31, coins: 20 }, level: 4, firstWinItem: 'eraser_armor' },
  { id: 'ink_octopus', name: 'Ink Octopus', blurb: 'Leaks from the teacher\'s fountain pen.', hp: 51, atk: 15, def: 4, luck: 6, skill: 'ink_combo', personality: 'tricky', tier: 'masterpiece', reward: { exp: 34, coins: 27 }, level: 5, firstWinItem: 'ink_cape' },
  { id: 'pencil_dragon', name: 'Pencil Dragon', blurb: 'Drawn on the last page. Breathes crayon fire.', hp: 67, atk: 20, def: 8, luck: 10, skill: 'crayon_smash', personality: 'chaotic', tier: 'masterpiece', reward: { exp: 53, coins: 31 }, level: 6, firstWinItem: 'golden_crayon' },
  // Past the last page: the sequel the kid drew on the back cover.
  { id: 'stapler_crab', name: 'Stapler Crab', blurb: 'Snaps shut on anything. Hitting it hurts your knuckles.', hp: 76, atk: 19, def: 9, luck: 6, skill: 'sharpener_spikes', personality: 'stubborn', tier: 'masterpiece', reward: { exp: 43, coins: 31 }, level: 7, firstWinItem: 'stapler_gauntlet' },
  { id: 'glue_monarch', name: 'Glue Monarch', blurb: 'Rules the art cupboard. Everything near it gets stuck.', hp: 88, atk: 19, def: 11, luck: 10, skill: 'glue_guard', personality: 'shy', tier: 'masterpiece', reward: { exp: 66, coins: 32 }, level: 8, firstWinItem: 'glue_boots' },
  { id: 'highlighter_wyvern', name: 'Highlighter Wyvern', blurb: 'Glows neon green. Drinks the ink of every hit.', hp: 66, atk: 22, def: 7, luck: 10, skill: 'highlighter_drain', personality: 'sharp', tier: 'masterpiece', reward: { exp: 54, coins: 34 }, level: 9, firstWinItem: 'neon_highlighter' },
  { id: 'principal_shadow', name: "Principal's Shadow", blurb: 'The final boss. Angrier with every scribble you land.', hp: 87, atk: 24, def: 11, luck: 12, skill: 'marker_rage', personality: 'copycat', tier: 'masterpiece', reward: { exp: 83, coins: 34 }, level: 10, firstWinItem: 'hall_pass' },
  // Season 1, "The Whole School": the rest of the building, levels 11-35.
  { id: 'crayon_wolf', name: "Crayon Wolf", blurb: "Howls in all 64 colours. Leaves wax on everything.", hp: 109, atk: 28, def: 13, luck: 9, skill: 'crayon_smash', personality: 'chaotic', tier: 'masterpiece', reward: { exp: 68, coins: 67 }, level: 11 },
  { id: 'paint_splat', name: "Paint Splat", blurb: "A puddle of poster paint that never dries.", hp: 114, atk: 28, def: 13, luck: 10, skill: 'eraser_mend', personality: 'shy', tier: 'masterpiece', reward: { exp: 76, coins: 70 }, level: 12, firstWinItem: 'paintbrush_staff' },
  { id: 'scissor_mantis', name: "Scissor Mantis", blurb: "Two pairs of safety scissors and zero safety.", hp: 112, atk: 30, def: 13, luck: 11, skill: 'sharp_edge', personality: 'sharp', tier: 'masterpiece', reward: { exp: 85, coins: 74 }, level: 13 },
  { id: 'easel_titan', name: "Easel Titan", blurb: "A wooden easel holding the worst painting ever drawn. Boss of the Art Room.", hp: 163, atk: 39, def: 18, luck: 16, skill: 'paper_shield', personality: 'stubborn', tier: 'masterpiece', reward: { exp: 131, coins: 97 }, level: 14, firstWinItem: 'sharpie_saber' },
  { id: 'bookworm', name: "Bookworm Serpent", blurb: "Eats whole chapters and burps footnotes.", hp: 128, atk: 35, def: 16, luck: 13, skill: 'highlighter_drain', personality: 'tricky', tier: 'masterpiece', reward: { exp: 107, coins: 81 }, level: 15 },
  { id: 'bookmark_bat', name: "Bookmark Bat", blurb: "Folds itself flat and hides between the pages.", hp: 136, atk: 36, def: 17, luck: 13, skill: 'doodle_dodge', personality: 'shy', tier: 'masterpiece', reward: { exp: 120, coins: 84 }, level: 16, firstWinItem: 'library_card' },
  { id: 'dusty_tome', name: "Dusty Tome", blurb: "An encyclopedia nobody has opened for years. Heavy and cranky.", hp: 138, atk: 37, def: 17, luck: 14, skill: 'glue_guard', personality: 'stubborn', tier: 'masterpiece', reward: { exp: 135, coins: 88 }, level: 17 },
  { id: 'shush_owl', name: "Shush Owl", blurb: "Guardian of the library. Every hit you land earns a loud SHHH. Boss of the Library.", hp: 198, atk: 40, def: 23, luck: 20, skill: 'marker_rage', personality: 'copycat', tier: 'masterpiece', reward: { exp: 230, coins: 115 }, level: 19, firstWinItem: 'cardigan_coat' },
  { id: 'beaker_blob', name: "Beaker Blob", blurb: "Bubbles over when it gets angry. It gets angry a lot.", hp: 156, atk: 40, def: 20, luck: 16, skill: 'ink_combo', personality: 'chaotic', tier: 'masterpiece', reward: { exp: 189, coins: 98 }, level: 20 },
  { id: 'magnet_mole', name: "Magnet Mole", blurb: "Pulls every paperclip in the room toward it.", hp: 162, atk: 40, def: 21, luck: 16, skill: 'sharpener_spikes', personality: 'sharp', tier: 'masterpiece', reward: { exp: 212, coins: 102 }, level: 21, firstWinItem: 'lab_coat' },
  { id: 'volcano_toad', name: "Volcano Toad", blurb: "A baking-soda science project that erupts on purpose.", hp: 168, atk: 40, def: 21, luck: 18, skill: 'crayon_smash', personality: 'tricky', tier: 'masterpiece', reward: { exp: 238, coins: 105 }, level: 22 },
  { id: 'calc_bot', name: "Calculator Bot", blurb: "Solves you for X. X is always \"lose\". Boss of the Science Lab.", hp: 200, atk: 40, def: 27, luck: 23, skill: 'lucky_star', personality: 'copycat', tier: 'masterpiece', reward: { exp: 407, coins: 132 }, level: 24, firstWinItem: 'calculator_club' },
  { id: 'dodgeball_ogre', name: "Dodgeball Ogre", blurb: "Throws hard. Throws even harder when it misses.", hp: 186, atk: 40, def: 25, luck: 19, skill: 'marker_rage', personality: 'stubborn', tier: 'masterpiece', reward: { exp: 334, coins: 116 }, level: 25 },
  { id: 'jumprope_snake', name: "Jump-Rope Snake", blurb: "Skips, trips and whips.", hp: 192, atk: 40, def: 26, luck: 20, skill: 'doodle_dodge', personality: 'tricky', tier: 'masterpiece', reward: { exp: 374, coins: 119 }, level: 26, firstWinItem: 'gym_jersey' },
  { id: 'whistle_hawk', name: "Whistle Hawk", blurb: "Its whistle stops every game. Even yours.", hp: 198, atk: 40, def: 26, luck: 20, skill: 'sharp_edge', personality: 'sharp', tier: 'masterpiece', reward: { exp: 419, coins: 123 }, level: 27 },
  { id: 'sneaker_kaiju', name: "Sneaker Kaiju", blurb: "A giant lost gym shoe that stomps the whole field. Boss of the Gym.", hp: 200, atk: 40, def: 30, luck: 26, skill: 'glue_guard', personality: 'stubborn', tier: 'masterpiece', reward: { exp: 716, coins: 150 }, level: 29, firstWinItem: 'golden_whistle' },
  { id: 'meatball_golem', name: "Meatball Golem", blurb: "Mystery meat, mystery strength.", hp: 200, atk: 40, def: 29, luck: 23, skill: 'eraser_mend', personality: 'stubborn', tier: 'masterpiece', reward: { exp: 588, coins: 133 }, level: 30 },
  { id: 'jelly_jiggler', name: "Jelly Jiggler", blurb: "Wobbles out of the way of almost every hit.", hp: 200, atk: 40, def: 30, luck: 23, skill: 'doodle_dodge', personality: 'chaotic', tier: 'masterpiece', reward: { exp: 659, coins: 137 }, level: 31, firstWinItem: 'science_medal' },
  { id: 'milk_knight', name: "Milk Carton Knight", blurb: "Expired, armoured and very sour.", hp: 200, atk: 40, def: 30, luck: 25, skill: 'paper_shield', personality: 'copycat', tier: 'masterpiece', reward: { exp: 827, coins: 144 }, level: 33, firstWinItem: 'lunch_tray_shield' },
  { id: 'mystery_hydra', name: "Mystery Meal Hydra", blurb: "Three heads, three trays, one terrible lunch. Boss of the Cafeteria.", hp: 200, atk: 40, def: 30, luck: 30, skill: 'highlighter_drain', personality: 'tricky', tier: 'masterpiece', reward: { exp: 1414, coins: 171 }, level: 35, firstWinItem: 'lunch_money' },
];

/**
 * Story chapters ("Quest Pages"): the preset monsters in story order. A stage
 * unlocks when the previous one is beaten; a chapter unlocks when the previous
 * chapter is complete. Finishing a chapter pays its reward once.
 */
export interface ChapterDef {
  id: string;
  title: string;
  subtitle: string;
  /** Short story text shown on the chapter page. */
  story: string;
  /** Preset monster ids in order; the last one is the chapter boss. */
  stages: string[];
  reward: { coins: number; item?: string };
}

export const CHAPTERS: ChapterDef[] = [
  {
    id: 'prologue',
    title: 'Prologue',
    subtitle: 'A New Journey',
    story: 'Something is scribbling in the margins of your notebook. Grab a pencil and find out what.',
    stages: ['gum_blob', 'homework_ghost', 'spitball_spider'],
    reward: { coins: 30 },
  },
  {
    id: 'forest',
    title: 'The Whispering Forest',
    subtitle: 'Chapter 2',
    story: 'The doodled trees whisper about an eraser beast and a dragon drawn on the last page.',
    stages: ['eraser_golem', 'ink_octopus', 'pencil_dragon'],
    reward: { coins: 60 },
  },
  {
    id: 'bridge',
    title: 'The Lost Bridge',
    subtitle: 'Chapter 3',
    story: 'Someone stapled and glued the bridge to the back cover shut. Unstick it!',
    stages: ['stapler_crab', 'glue_monarch'],
    reward: { coins: 90 },
  },
  {
    id: 'office',
    title: "The Principal's Office",
    subtitle: 'Final Chapter',
    story: 'A neon glow leaks from under the office door. The final page awaits.',
    stages: ['highlighter_wyvern', 'principal_shadow'],
    reward: { coins: 150 },
  },
  {
    id: 'art_room',
    title: "The Art Room",
    subtitle: 'Chapter 5',
    story: "Paint is moving on its own and the scissors are snipping at shadows. Something in the Art Room is drawing back.",
    stages: ['crayon_wolf', 'paint_splat', 'scissor_mantis', 'easel_titan'],
    reward: { coins: 200 },
  },
  {
    id: 'library',
    title: "The Silent Library",
    subtitle: 'Chapter 6',
    story: "Books are flying off the shelves and every page is whispering. Find out who woke the library up.",
    stages: ['bookworm', 'bookmark_bat', 'dusty_tome', 'shush_owl'],
    reward: { coins: 260 },
  },
  {
    id: 'lab',
    title: "The Science Lab",
    subtitle: 'Chapter 7',
    story: "An experiment bubbled over and now the lab is alive. Put on your goggles.",
    stages: ['beaker_blob', 'magnet_mole', 'volcano_toad', 'calc_bot'],
    reward: { coins: 320 },
  },
  {
    id: 'gym',
    title: "The Gym",
    subtitle: 'Chapter 8',
    story: "The equipment cupboard burst open and the whole gym wants a rematch.",
    stages: ['dodgeball_ogre', 'jumprope_snake', 'whistle_hawk', 'sneaker_kaiju'],
    reward: { coins: 400 },
  },
  {
    id: 'cafeteria',
    title: "The Cafeteria",
    subtitle: 'Chapter 9',
    story: "Today's lunch is fighting back. Clear the trays before the bell rings.",
    stages: ['meatball_golem', 'jelly_jiggler', 'milk_knight', 'mystery_hydra'],
    reward: { coins: 500 },
  },
];

export type ItemSlot = 'weapon' | 'armor' | 'charm';

export interface ItemDef {
  id: string;
  name: string;
  slot: ItemSlot;
  bonus: Partial<{ hp: number; atk: number; def: number; luck: number }>;
  price: number;
  note: string;
}

export const ITEMS: ItemDef[] = [
  { id: 'pencil_sword', name: 'Pencil Sword', slot: 'weapon', bonus: { atk: 2 }, price: 100, note: 'HB lead. Very pointy.' },
  { id: 'golden_crayon', name: 'Golden Crayon', slot: 'weapon', bonus: { atk: 4, luck: 1 }, price: 360, note: 'Only for the best drawers.' },
  { id: 'compass_spear', name: 'Compass Spear', slot: 'weapon', bonus: { atk: 3, def: -1 }, price: 180, note: 'Draws perfect circles. Ouch.' },
  { id: 'cardboard_shield', name: 'Cardboard Shield', slot: 'armor', bonus: { def: 1, hp: 4 }, price: 80, note: 'Cut from a cereal box.' },
  { id: 'eraser_armor', name: 'Eraser Armor', slot: 'armor', bonus: { hp: 10, def: 1 }, price: 150, note: 'Squishy but reliable.' },
  { id: 'ink_cape', name: 'Ink Cape', slot: 'armor', bonus: { def: 2, luck: 2 }, price: 230, note: 'Still a bit wet.' },
  { id: 'lucky_sticker', name: 'Lucky Clover Sticker', slot: 'charm', bonus: { luck: 4 }, price: 90, note: 'Smells like strawberries.' },
  { id: 'gold_star', name: 'Gold Star', slot: 'charm', bonus: { atk: 1, def: 1, luck: 1 }, price: 140, note: 'Teacher said "Excellent!"' },
  { id: 'paper_crown', name: 'Paper Crown', slot: 'charm', bonus: { hp: 8, luck: 2 }, price: 250, note: 'From the birthday party.' },
  { id: 'stapler_gauntlet', name: 'Stapler Gauntlet', slot: 'weapon', bonus: { atk: 3, hp: 4 }, price: 210, note: 'Ka-chunk!' },
  { id: 'neon_highlighter', name: 'Neon Highlighter', slot: 'weapon', bonus: { atk: 5, luck: 2 }, price: 510, note: 'Glows in the dark. Never runs out.' },
  { id: 'glue_boots', name: 'Glue Boots', slot: 'armor', bonus: { def: 3, hp: 6 }, price: 330, note: 'You are not going anywhere.' },
  { id: 'marker_cape', name: 'Marker Cape', slot: 'armor', bonus: { def: 2, hp: 12, luck: 1 }, price: 450, note: 'Permanent marker. Do not wash.' },
  { id: 'rubber_band', name: 'Rubber Band', slot: 'charm', bonus: { atk: 1, luck: 3 }, price: 150, note: 'Snap!' },
  { id: 'hall_pass', name: 'Hall Pass', slot: 'charm', bonus: { hp: 6, def: 1, luck: 3 }, price: 420, note: 'Lets you go anywhere. Even the staff room.' },
  // Season 1: first-win rewards from the new chapters, also in the school shop.
  { id: 'sharpie_saber', name: "Sharpie Saber", slot: 'weapon', bonus: { atk: 6, luck: 1 }, price: 880, note: "Permanent damage. Literally." },
  { id: 'paintbrush_staff', name: "Paintbrush Staff", slot: 'weapon', bonus: { atk: 5, hp: 10 }, price: 960, note: "Paints you a little healthier." },
  { id: 'protractor_axe', name: "Protractor Axe", slot: 'weapon', bonus: { atk: 7, def: -1 }, price: 1040, note: "Cuts at exactly 45 degrees." },
  { id: 'calculator_club', name: "Calculator Club", slot: 'weapon', bonus: { atk: 8, luck: 1 }, price: 1280, note: "Adds up to a lot of damage." },
  { id: 'cardigan_coat', name: "Cardigan Coat", slot: 'armor', bonus: { def: 4, hp: 14 }, price: 920, note: "Knitted by the librarian. Very cosy." },
  { id: 'lab_coat', name: "Lab Coat", slot: 'armor', bonus: { def: 4, luck: 3 }, price: 1040, note: "Stain-proof. Mostly." },
  { id: 'gym_jersey', name: "Gym Jersey", slot: 'armor', bonus: { hp: 24, def: 3 }, price: 1200, note: "Number 1, obviously." },
  { id: 'lunch_tray_shield', name: "Lunch Tray Shield", slot: 'armor', bonus: { def: 6, hp: 10 }, price: 1360, note: "Survived a thousand lunches." },
  { id: 'library_card', name: "Library Card", slot: 'charm', bonus: { luck: 5, def: 2 }, price: 840, note: "Never overdue." },
  { id: 'golden_whistle', name: "Golden Whistle", slot: 'charm', bonus: { atk: 3, luck: 4 }, price: 1120, note: "Everyone stops and listens." },
  { id: 'science_medal', name: "Science Fair Medal", slot: 'charm', bonus: { hp: 12, atk: 2, luck: 2 }, price: 1200, note: "First place, volcano category." },
  { id: 'lunch_money', name: "Lunch Money", slot: 'charm', bonus: { luck: 6, hp: 8 }, price: 1320, note: "Jingles in your pocket. Feels lucky." },
];

export function itemDef(id: string): ItemDef | undefined {
  return ITEMS.find((i) => i.id === id);
}

export type QuestMetric =
  | 'wins'
  | 'battles'
  | 'monstersCreated'
  | 'bestCombo'
  | 'rockWins'
  | 'paperWins'
  | 'scissorsWins'
  | 'pvpBattles'
  | 'shares'
  | 'level'
  | 'presetsDefeated'
  // Counted for daily quests: contest votes, Hall of Fame wins, 3-star story wins.
  | 'votes'
  | 'hallWins'
  | 'threeStarWins'
  // Community: dares sent and beaten, boss battles, stickers sent to artists.
  | 'daresSent'
  | 'daresBeaten'
  | 'bossBattles'
  | 'reactionsSent'
  // Highest Homework Tower floor cleared.
  | 'towerBest'
  // Lucky dice rolled (battle dice, victory rolls and the dice board).
  | 'diceRolls'
  // Times round the dice board.
  | 'boardLaps';

export interface QuestDef {
  id: string;
  title: string;
  note: string;
  metric: QuestMetric;
  goal: number;
  reward: { exp: number; coins: number; item?: string };
}

export const QUESTS: QuestDef[] = [
  { id: 'first_blood', title: 'First Recess Battle', note: 'Win your first battle.', metric: 'wins', goal: 1, reward: { exp: 20, coins: 15 } },
  { id: 'artist', title: 'Back-of-the-Book Artist', note: 'Draw and save your own monster.', metric: 'monstersCreated', goal: 1, reward: { exp: 25, coins: 20 } },
  { id: 'rock_solid', title: 'Rock Solid', note: 'Win 5 rounds with Rock.', metric: 'rockWins', goal: 5, reward: { exp: 20, coins: 10 } },
  { id: 'paper_trail', title: 'Paper Trail', note: 'Win 5 rounds with Paper.', metric: 'paperWins', goal: 5, reward: { exp: 20, coins: 10 } },
  { id: 'snip_snip', title: 'Snip Snip', note: 'Win 5 rounds with Scissors.', metric: 'scissorsWins', goal: 5, reward: { exp: 20, coins: 10 } },
  { id: 'combo_3', title: 'On a Roll', note: 'Reach a 3-round combo.', metric: 'bestCombo', goal: 3, reward: { exp: 30, coins: 15, item: 'gold_star' } },
  { id: 'veteran', title: 'Playground Veteran', note: 'Fight 10 battles.', metric: 'battles', goal: 10, reward: { exp: 60, coins: 40 } },
  { id: 'monster_hunter', title: 'Monster Hunter', note: 'Defeat 6 different monsters in the back of the book.', metric: 'presetsDefeated', goal: 6, reward: { exp: 120, coins: 80, item: 'paper_crown' } },
  { id: 'show_off', title: 'Show and Tell', note: 'Share a monster with a friend.', metric: 'shares', goal: 1, reward: { exp: 15, coins: 15 } },
  { id: 'rival', title: 'Classroom Rival', note: 'Battle a friend online.', metric: 'pvpBattles', goal: 1, reward: { exp: 40, coins: 25 } },
  { id: 'level_5', title: 'Growing Up', note: 'Reach level 5.', metric: 'level', goal: 5, reward: { exp: 0, coins: 50, item: 'compass_spear' } },
  { id: 'last_page', title: 'Past the Last Page', note: 'Defeat every monster in the book, back cover included.', metric: 'presetsDefeated', goal: 10, reward: { exp: 200, coins: 120, item: 'marker_cape' } },
  { id: 'level_10', title: 'Top of the Class', note: 'Reach level 10.', metric: 'level', goal: 10, reward: { exp: 0, coins: 100, item: 'rubber_band' } },
  { id: 'combo_5', title: 'Unstoppable', note: 'Reach a 5-round combo.', metric: 'bestCombo', goal: 5, reward: { exp: 60, coins: 30 } },
  { id: 'recess_legend', title: 'Recess Legend', note: 'Fight 25 battles.', metric: 'battles', goal: 25, reward: { exp: 120, coins: 70 } },
  { id: 'rock_star', title: 'Rock Star', note: 'Win 20 rounds with Rock.', metric: 'rockWins', goal: 20, reward: { exp: 50, coins: 25 } },
  { id: 'art_critic', title: "Art Critic", note: "Defeat every monster in the Art Room.", metric: 'presetsDefeated', goal: 14, reward: { exp: 150, coins: 120 } },
  { id: 'bookworm_hunter', title: "Quiet Please", note: "Defeat every monster in the Silent Library.", metric: 'presetsDefeated', goal: 18, reward: { exp: 200, coins: 160 } },
  { id: 'mad_scientist', title: "Mad Scientist", note: "Defeat every monster in the Science Lab.", metric: 'presetsDefeated', goal: 22, reward: { exp: 260, coins: 200, item: 'protractor_axe' } },
  { id: 'mvp', title: "Most Valuable Player", note: "Defeat every monster in the Gym.", metric: 'presetsDefeated', goal: 26, reward: { exp: 320, coins: 260 } },
  { id: 'whole_school', title: "The Whole School", note: "Defeat every monster in the school, cafeteria included.", metric: 'presetsDefeated', goal: 30, reward: { exp: 400, coins: 400 } },
  { id: 'level_15', title: "Big Kid", note: "Reach level 15.", metric: 'level', goal: 15, reward: { exp: 0, coins: 150 } },
  { id: 'level_20', title: "Hall Monitor", note: "Reach level 20.", metric: 'level', goal: 20, reward: { exp: 0, coins: 200 } },
  { id: 'level_30', title: "Senior", note: "Reach level 30.", metric: 'level', goal: 30, reward: { exp: 0, coins: 300 } },
  { id: 'level_50', title: "Paper Legend", note: "Reach level 50.", metric: 'level', goal: 50, reward: { exp: 0, coins: 1000 } },
  { id: 'tower_5', title: "First Floors", note: "Climb to floor 5 of the Homework Tower.", metric: 'towerBest', goal: 5, reward: { exp: 100, coins: 80 } },
  { id: 'tower_10', title: "Halfway Up", note: "Climb to floor 10 of the Homework Tower.", metric: 'towerBest', goal: 10, reward: { exp: 200, coins: 150 } },
  { id: 'tower_25', title: "Tower Climber", note: "Climb to floor 25 of the Homework Tower.", metric: 'towerBest', goal: 25, reward: { exp: 400, coins: 300 } },
  { id: 'tower_50', title: "Top of the Tower", note: "Climb to floor 50 of the Homework Tower.", metric: 'towerBest', goal: 50, reward: { exp: 800, coins: 600 } },
  { id: 'century', title: "Hundred Wins", note: "Win 100 battles.", metric: 'wins', goal: 100, reward: { exp: 300, coins: 250 } },
  { id: 'dice_10', title: 'Feeling Lucky', note: 'Roll the lucky dice 10 times.', metric: 'diceRolls', goal: 10, reward: { exp: 60, coins: 60 } },
  { id: 'board_lap', title: 'Board Game Night', note: 'Go all the way round the dice board.', metric: 'boardLaps', goal: 1, reward: { exp: 80, coins: 80 } },
  { id: 'board_laps_10', title: 'Board Game Champion', note: 'Go round the dice board 10 times.', metric: 'boardLaps', goal: 10, reward: { exp: 400, coins: 400 } },
  { id: 'dice_100', title: 'Dice Master', note: 'Roll the lucky dice 100 times.', metric: 'diceRolls', goal: 100, reward: { exp: 300, coins: 300 } },
];
